import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Register() {
  const { register, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", confirm: "" });
  const [agree, setAgree] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPhone = (v) => set("phone", v.replace(/\D/g, "").slice(0, 10));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^[6-9]\d{9}$/.test(form.phone)) return setError("Please enter a valid 10-digit mobile number (starts with 6-9).");
    if (form.password !== form.confirm) return setError("Passwords do not match.");
    if (form.password.length < 6) return setError("Password must be at least 6 characters.");
    if (!agree) return setError("Please accept the Terms to continue.");
    setLoading(true);
    try {
      await register({ name: form.name.trim(), email: form.email.trim(), phone: form.phone, password: form.password, role: "buyer" });
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Seo title="Create Account — The Pataudi" description="Register on The Pataudi to post free listings for properties, vehicles and services." />
      <Header />
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md bg-card rounded-card shadow-soft border border-border p-7" data-testid="register-card">
          <h1 className="font-display font-bold text-2xl text-ink">Create your account</h1>
          <p className="text-sm text-muted-foreground mt-1">Join The Pataudi and start posting listings.</p>

          {error && <div className="mt-4 text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2" data-testid="register-error">{error}</div>}

          <form onSubmit={submit} className="mt-5 space-y-4">
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input id="name" required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Enter your full name" className="mt-1.5 rounded-sm" data-testid="register-name" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Enter your email address" className="mt-1.5 rounded-sm" data-testid="register-email" />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <div className="relative mt-1.5">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-muted-foreground select-none">+91</span>
                <Input id="phone" type="tel" inputMode="numeric" maxLength={10} required value={form.phone} onChange={(e) => setPhone(e.target.value)} placeholder="Enter 10-digit mobile number" className="rounded-sm pl-12" data-testid="register-phone" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-1.5">
                  <Input id="password" type={showPwd ? "text" : "password"} required value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="Min 6 characters" className="rounded-sm pr-9" data-testid="register-password" />
                  <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute inset-y-0 right-0 flex items-center px-2.5 text-muted-foreground hover:text-ink" aria-label={showPwd ? "Hide password" : "Show password"} data-testid="register-toggle-password" tabIndex={-1}>
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="confirm">Confirm</Label>
                <Input id="confirm" type={showPwd ? "text" : "password"} required value={form.confirm} onChange={(e) => set("confirm", e.target.value)} placeholder="Re-enter password" className="mt-1.5 rounded-sm" data-testid="register-confirm" />
              </div>
            </div>
            <label className="flex items-start gap-2 text-sm text-ink">
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 accent-[#1B4F72]" data-testid="register-terms" />
              <span>I agree to the Terms of Service and Privacy Policy.</span>
            </label>
            <Button type="submit" disabled={loading} className="w-full bg-primary hover:bg-primary/90 text-white rounded-sm h-11" data-testid="register-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Account"}
            </Button>
          </form>

          <p className="text-sm text-center text-muted-foreground mt-5">
            Already have an account? <Link to="/login" className="text-primary font-medium hover:underline" data-testid="go-login">Login</Link>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
